import type { MeetingStatus, Outcome, SentimentLevel, TipType } from '@copilot/shared';
import { Badge, type BadgeTone } from '../../components';
import { OUTCOME_LABELS, SENTIMENT_LABELS, STATUS_LABELS, TIP_TYPE_LABELS } from '../../lib/labels';

const OUTCOME_TONES: Record<Outcome, BadgeTone> = {
  discovery: 'neutral',
  follow_up_needed: 'warn',
  proposal_requested: 'accent',
  negotiation: 'accent',
  won: 'good',
  lost: 'danger',
  other: 'neutral',
};

export function OutcomeBadge({ outcome }: { outcome: Outcome | null }) {
  if (!outcome) return <span className="text-xs text-muted">Not set</span>;
  return <Badge tone={OUTCOME_TONES[outcome]}>{OUTCOME_LABELS[outcome]}</Badge>;
}

export function MeetingStatusBadge({ status }: { status: MeetingStatus }) {
  const tone: BadgeTone = status === 'live' ? 'danger' : status === 'reviewed' ? 'good' : 'neutral';
  return (
    <Badge tone={tone}>
      {status === 'live' && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />}
      {STATUS_LABELS[status]}
    </Badge>
  );
}

const TIP_TONES: Record<TipType, BadgeTone> = { term: 'accent', say: 'good', note: 'neutral', answer: 'warn' };

export function TipTypeBadge({ type }: { type: TipType }) {
  return <Badge tone={TIP_TONES[type]}>{TIP_TYPE_LABELS[type]}</Badge>;
}

const SENTIMENT_TONES: Record<SentimentLevel, BadgeTone> = {
  very_positive: 'good',
  positive: 'good',
  neutral: 'neutral',
  negative: 'danger',
  very_negative: 'danger',
  unknown: 'neutral',
};

/** Client sentiment. The label is always shown as text; color is never the only signal. */
export function SentimentBadge({ level }: { level: SentimentLevel | null }) {
  if (!level) return <span className="text-xs text-muted">—</span>;
  return <Badge tone={SENTIMENT_TONES[level]}>{SENTIMENT_LABELS[level]}</Badge>;
}
